/**
 * Internal engine behind LineChart and BarChart (use those).
 *
 * Props (CartesianProps):
 * - caption: accessible title, also the caption of the hidden data table.
 * - labels: x label per point (already formatted, e.g. "12 Oct"); series[].values align with it.
 * - series: one or two series { key, label, values }. The second one is dashed (line) / striped (bar).
 * - formatValue: number -> text for tooltip and table (money, minutes). formatTick: optional shorter form for the y axis.
 * - labelHeading: header of the first table column (default "Item").
 * - loading / height (default 220) / className.
 *
 * Keyboard: the chart is one tab stop; Left/Right/Home/End move between points, Escape closes the tooltip.
 * Pointer and touch: hover, tap or drag horizontally. No animation anywhere.
 */
import clsx from 'clsx';
import { KeyboardEvent, PointerEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartState, DataTable, Legend, PatternDef, Tooltip } from './chartKit';
import { AXIS, ChartSeries, GRID, MUTED, SURFACE, clamp, colorOf, fillFor, niceTicks, tickChars, uidOf, useWidth } from './chartUtils';

export interface CartesianProps {
    caption: string;
    labels: string[];
    series: ChartSeries[];
    formatValue: (n: number) => string;
    formatTick?: (n: number) => string;
    /** Characters reserved for the y axis labels; set the same value on stacked charts so their plots line up. */
    axisChars?: number;
    labelHeading?: string;
    loading?: boolean;
    height?: number;
    className?: string;
}

const M = { top: 8, right: 12, bottom: 26 };

export const CartesianChart = ({
    kind, area, caption, labels, series: allSeries, formatValue, formatTick, axisChars, labelHeading, loading, height = 220, className,
}: CartesianProps & { kind: 'line' | 'bar'; area?: boolean }) => {
    const { t } = useTranslation('charts');
    const uid = uidOf(useId());
    const { ref, width } = useWidth();
    const svgRef = useRef<SVGSVGElement>(null);
    const [active, setActive] = useState<number | null>(null);

    const series = allSeries.slice(0, 2);
    const n = labels.length;
    const tick = formatTick ?? formatValue;
    const maxValue = series.reduce((m, s) => s.values.reduce((a, v) => Math.max(a, v), m), 0);
    const ticks = niceTicks(maxValue);
    const top = ticks[ticks.length - 1] ?? 1;
    const left = clamp((axisChars ?? tickChars(maxValue, tick)) * 6.5 + 12, 28, 84);
    const plotW = Math.max(width - left - M.right, 10);
    const plotH = Math.max(height - M.top - M.bottom, 10);
    const y = (v: number) => M.top + plotH - (v / top) * plotH;
    const isLine = kind === 'line';
    const stepX = isLine ? (n > 1 ? plotW / (n - 1) : 0) : plotW / Math.max(n, 1);
    const xAt = (i: number) => (isLine ? (n > 1 ? left + i * stepX : left + plotW / 2) : left + (i + 0.5) * stepX);
    const labelW = Math.max(...labels.map((l) => l.length), 1) * 6.5 + 14;
    const every = Math.max(1, Math.ceil(labelW / (stepX || plotW)));
    const baseY = y(0);

    const indexAt = (e: PointerEvent): number => {
        const rect = svgRef.current?.getBoundingClientRect();
        const px = e.clientX - (rect?.left ?? 0) - left;
        if (n <= 1 || stepX === 0) return 0;
        return clamp(isLine ? Math.round(px / stepX) : Math.floor(px / stepX), 0, n - 1);
    };
    const onKey = (e: KeyboardEvent) => {
        const move = (to: number) => {
            e.preventDefault();
            setActive(clamp(to, 0, n - 1));
        };
        const cur = active ?? n - 1;
        if (e.key === 'ArrowRight') move(cur + 1);
        else if (e.key === 'ArrowLeft') move(cur - 1);
        else if (e.key === 'Home') move(0);
        else if (e.key === 'End') move(n - 1);
        else if (e.key === 'Escape') setActive(null);
    };

    const rows = labels.map((l, i) => [l, ...series.map((s) => formatValue(s.values[i] ?? 0))]);
    const table = <DataTable caption={caption} headers={[labelHeading ?? t('item'), ...series.map((s) => s.label)]} rows={rows} />;
    const shown = active !== null && active < n ? active : null;
    const summary = shown === null ? '' : rows[shown]?.map((c, i) => (i === 0 ? c : `${series[i - 1]?.label ?? ''} ${c}`)).join(', ') ?? '';

    return (
        <div className={className}>
            <ChartState loading={loading} empty={n === 0 || series.length === 0} height={height}>
                <div ref={ref} className="relative">
                    {series.length > 1 && <Legend uid={uid} entries={series.map((s) => ({ key: s.key, label: s.label }))} />}
                    <div
                        tabIndex={0}
                        role="group"
                        aria-label={`${caption}. ${t('keyboardHint')}`}
                        className={clsx('relative rounded-md', series.length > 1 && 'mt-2')}
                        style={{ touchAction: 'pan-y' }}
                        onKeyDown={onKey}
                        onFocus={() => setActive((a) => a ?? n - 1)}
                        onBlur={() => setActive(null)}
                        onPointerDown={(e) => setActive(indexAt(e))}
                        onPointerMove={(e) => setActive(indexAt(e))}
                        onPointerLeave={(e) => e.pointerType === 'mouse' && document.activeElement !== e.currentTarget && setActive(null)}
                    >
                        <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false" className="block">
                            <defs>{series.map((_, i) => <PatternDef key={i} uid={uid} i={i} />)}</defs>
                            {ticks.map((v) => (
                                <g key={v}>
                                    <line x1={left} x2={left + plotW} y1={y(v)} y2={y(v)} stroke={v === 0 ? AXIS : GRID} />
                                    <text x={left - 6} y={y(v)} textAnchor="end" dominantBaseline="central" fontSize="11" fill={MUTED}>
                                        {tick(v)}
                                    </text>
                                </g>
                            ))}
                            {labels.map((l, i) =>
                                i % every === 0 ? (
                                    <text key={i} x={xAt(i)} y={height - 8} textAnchor="middle" fontSize="11" fill={MUTED}>
                                        {l}
                                    </text>
                                ) : null,
                            )}
                            {shown !== null && (isLine ? (
                                <line x1={xAt(shown)} x2={xAt(shown)} y1={M.top} y2={baseY} stroke={AXIS} strokeDasharray="2 3" />
                            ) : (
                                <rect x={xAt(shown) - stepX / 2} y={M.top} width={stepX} height={plotH} fill={GRID} />
                            ))}
                            {series.map((s, si) => {
                                const color = colorOf(si);
                                if (!isLine) {
                                    const group = Math.min(stepX * 0.72, 48 * series.length);
                                    const bw = group / series.length;
                                    return (
                                        <g key={s.key}>
                                            {s.values.slice(0, n).map((v, i) => (
                                                <rect
                                                    key={i}
                                                    x={xAt(i) - group / 2 + si * bw}
                                                    y={y(v)}
                                                    width={Math.max(bw - (bw > 4 ? 1 : 0), 0.5)}
                                                    height={Math.max(baseY - y(v), v > 0 ? 1 : 0)}
                                                    fill={fillFor(uid, si)}
                                                    stroke={si > 0 ? color : undefined}
                                                />
                                            ))}
                                        </g>
                                    );
                                }
                                const pts = s.values.slice(0, n).map((v, i) => `${xAt(i).toFixed(1)} ${y(v).toFixed(1)}`);
                                const d = `M${pts.join('L')}`;
                                return (
                                    <g key={s.key}>
                                        {area && si === 0 && n > 1 && (
                                            <path d={`${d}L${xAt(n - 1).toFixed(1)} ${baseY}L${xAt(0).toFixed(1)} ${baseY}Z`} fill={color} opacity="0.14" />
                                        )}
                                        {n > 1 && (
                                            <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={si > 0 ? '6 4' : undefined} />
                                        )}
                                        {(n === 1 || n <= 31) &&
                                            s.values.slice(0, n).map((v, i) => (
                                                <circle key={i} cx={xAt(i)} cy={y(v)} r="3" fill={SURFACE} stroke={color} strokeWidth="2" />
                                            ))}
                                        {shown !== null && <circle cx={xAt(shown)} cy={y(s.values[shown] ?? 0)} r="5" fill={color} stroke={SURFACE} strokeWidth="2" />}
                                    </g>
                                );
                            })}
                        </svg>
                        {shown !== null && (
                            <Tooltip
                                x={xAt(shown)}
                                width={width}
                                title={labels[shown] ?? ''}
                                rows={series.map((s, si) => ({
                                    key: s.key,
                                    label: series.length > 1 ? s.label : undefined,
                                    value: formatValue(s.values[shown] ?? 0),
                                    swatch: series.length > 1 ? { uid, i: si } : undefined,
                                }))}
                            />
                        )}
                    </div>
                    <span className="sr-only" aria-live="polite">{summary}</span>
                </div>
            </ChartState>
            {table}
        </div>
    );
};

